import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Globe2, DollarSign, FileText, Plane, Heart, Shield,
  CheckCircle, ArrowRight, MapPin, Stethoscope, Clock
} from "lucide-react";

const IMAGES = {
  globe: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/157d58af0c873689086ab59e871ccbef313009bf79a9876c49d9d65e611c1cda.png",
};

const whyIndia = [
  { icon: Stethoscope, title: "World-Class Doctors", desc: "Kerala's hospitals are staffed by specialists trained at top global institutions." },
  { icon: DollarSign, title: "60-90% Cost Savings", desc: "Treatments in Kerala cost a fraction of what they do in the US, Europe and the Middle East." },
  { icon: Shield, title: "International Accreditation", desc: "Kerala's leading hospitals hold JCI and NABH accreditation, meeting global standards." },
  { icon: Globe2, title: "English Speaking", desc: "Kerala has 96%+ literacy and English is widely spoken in hospitals — communication is effortless." },
  { icon: Heart, title: "No Waiting Lists", desc: "Get treated immediately without the long waiting periods common in many countries." },
  { icon: Clock, title: "Advanced Technology", desc: "Kerala's hospitals use the latest medical technology including robotic surgery and AI diagnostics." },
];

const visaSteps = [
  { step: "1", title: "Get Medical Invitation", desc: "Receive an invitation letter from the hospital confirming your treatment." },
  { step: "2", title: "Apply for Medical Visa", desc: "Apply for an Indian Medical Visa (M-Visa) online or at your nearest Indian embassy." },
  { step: "3", title: "Submit Documents", desc: "Provide passport, medical reports, hospital letter, and travel itinerary." },
  { step: "4", title: "Visa Processing", desc: "Processing typically takes 3-5 working days. Express options available." },
  { step: "5", title: "Travel to Kerala, India", desc: "Our team assists with flight booking and airport pickup upon arrival." },
];

const travelTips = [
  "Carry all medical records and prescriptions in your hand luggage",
  "Inform your insurance company about medical travel plans",
  "Get travel insurance that covers medical tourism",
  "Pack comfortable clothing suitable for Indian climate",
  "Carry sufficient medication for your trip duration",
  "Download our recommended apps for translation and navigation",
  "Keep emergency contact numbers of your coordinator",
  "Arrive 1-2 days before your scheduled procedure",
];

export default function GuidePage() {
  const { t } = useLanguage();
  const whyIndiaItems = t("guide.whyIndia");
  const visaStepItems = t("guide.visaSteps");
  const tipItems = t("guide.tips");
  return (
    <div data-testid="guide-page">
      <SEO title="Medical Tourism Guide" description="Complete guide to medical tourism in Kerala, India - why Kerala, cost comparison, visa process, travel tips. Save up to 90% on medical treatment." path="/guide" keywords="medical tourism guide India, medical visa India, treatment cost comparison, travel tips medical tourism" />
      {/* Hero */}
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <span className="overline">{t("guide.overline")}</span>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-[#03045e] mt-3 mb-6 tracking-tight">
                {t("guide.title")} <span className="font-medium text-[#00A4A9]">{t("guide.titleHighlight")}</span>
              </h1>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-8">
                {t("guide.desc")}
              </p>
              <Link to="/contact" data-testid="guide-cta-btn">
                <Button className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white btn-hover">
                  {t("guide.startJourney")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </FadeIn>
            <FadeIn delay={200}>
              <img src={IMAGES.globe} alt="Medical tourism globe" className="rounded-3xl w-full" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Why India */}
      <section className="section-padding bg-white" data-testid="why-india-section">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="overline">{t("guide.whyIndiaOverline")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight">
                {t("guide.whyIndiaTitle")}
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(Array.isArray(whyIndiaItems) ? whyIndiaItems : whyIndia).map((item, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="bg-white border border-slate-200 rounded-2xl p-7 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`why-india-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#e0f2fe] flex items-center justify-center mb-4">
                    {(() => { const IconComp = item.icon || whyIndia[i % whyIndia.length].icon; return <IconComp className="w-6 h-6 text-[#00A4A9]" />; })()}
                  </div>
                  <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{item.t || item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.d || item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="section-padding bg-[#F9F9F8]" data-testid="guide-cost-section">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="overline">{t("guide.costOverline")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                {t("guide.costTitle")}
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {[
                { country: "United States", multiplier: "10x", color: "bg-red-50 border-red-100 text-red-700" },
                { country: "Europe", multiplier: "6x", color: "bg-blue-50 border-blue-100 text-blue-700" },
                { country: "Middle East", multiplier: "4x", color: "bg-amber-50 border-amber-100 text-amber-700" },
                { country: "Kerala, India", multiplier: "1x", color: "bg-[#e0f2fe] border-[#00A4A9]/20 text-[#00A4A9]" },
              ].map((c) => (
                <div key={c.country} className={`rounded-2xl border p-6 text-center ${c.color}`}>
                  <div className="font-outfit text-4xl font-bold mb-2">{c.multiplier}</div>
                  <div className="font-outfit font-medium">{c.country}</div>
                  <div className="text-xs mt-1 opacity-75">relative treatment cost</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Visa Process */}
      <section className="section-padding bg-white" data-testid="visa-section">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="overline">{t("guide.visaOverline")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                {t("guide.visaTitle")}
              </h2>
              <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
                {t("guide.visaDesc")}
              </p>
            </div>
          </FadeIn>
          <div className="max-w-3xl mx-auto space-y-6">
            {(Array.isArray(visaStepItems) ? visaStepItems : visaSteps).map((s, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="flex gap-5 items-start" data-testid={`visa-step-${i}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00A4A9] flex items-center justify-center">
                    <span className="font-outfit text-sm font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-outfit font-medium text-[#03045e] mb-1">{s.t || s.title}</h3>
                    <p className="text-sm text-slate-600">{s.d || s.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="section-padding bg-[#F9F9F8]" data-testid="travel-tips-section">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="overline">{t("guide.tipsOverline")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                {t("guide.tipsTitle")}
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {(Array.isArray(tipItems) ? tipItems : travelTips).map((tip, i) => (
              <FadeIn key={i} delay={i * 50}>
                <div className="flex items-start gap-3 bg-white rounded-xl p-4 border border-slate-200" data-testid={`travel-tip-${i}`}>
                  <CheckCircle className="w-5 h-5 text-[#00A4A9] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600">{tip}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA replaced by sitewide PreFooterCTA */}
    </div>
  );
}
